import React, { useEffect, useState } from "react";
import { Calendar, MapPin, Plus, Trash2, Save, Users, X } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";

const TYPES = [
  { v: "rehearsal_dinner", l: "Rehearsal Dinner" },
  { v: "welcome_party", l: "Welcome Party" },
  { v: "ceremony", l: "Ceremony" },
  { v: "reception", l: "Reception" },
  { v: "after_party", l: "After Party" },
  { v: "brunch", l: "Brunch" },
  { v: "shower", l: "Shower" },
  { v: "bachelor_bachelorette", l: "Bach Party" },
  { v: "other", l: "Other" },
];

const EMPTY = { type: "ceremony", name: "", event_datetime: "", venue: "", address: "", description: "", dress_code: "", capacity: 0, color_accent: "#C8A96B" };

export default function EventsPage() {
  const [w, setW] = useState(null);
  const [events, setEvents] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  // Per-guest invites manager
  const [showInvites, setShowInvites] = useState(false);
  const [guests, setGuests] = useState([]);
  const [invitesByGuest, setInvitesByGuest] = useState({});

  async function load() {
    const ww = await api.get("/weddings/mine");
    setW(ww.data);
    const e = await api.get(`/weddings/${ww.data.wedding_id}/events`);
    setEvents(e.data);
  }
  useEffect(() => { load().catch(() => {}); }, []);

  async function loadInvites() {
    if (!w) return;
    const g = await api.get(`/weddings/${w.wedding_id}/guests`);
    setGuests(g.data || []);
    // Fetch each guest's existing invites in parallel
    const all = await Promise.all(
      (g.data || []).map((gu) =>
        api.get(`/weddings/${w.wedding_id}/guests/${gu.guest_id}/event-invites`).then((r) => [gu.guest_id, r.data || []])
      )
    );
    setInvitesByGuest(Object.fromEntries(all.map(([gid, list]) => [gid, list.map((i) => i.event_id)])));
  }

  function openNew() {
    setForm(EMPTY);
    setEditing(null);
    setShowForm(true);
  }
  function openEdit(e) {
    setForm({ ...EMPTY, ...e });
    setEditing(e.event_id);
    setShowForm(true);
  }

  async function save(e) {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = { ...form, capacity: Number(form.capacity || 0) };
      // Strip non-API fields
      delete payload.event_id; delete payload.wedding_id; delete payload.created_at;
      if (editing) {
        await api.patch(`/weddings/${w.wedding_id}/events/${editing}`, payload);
      } else {
        await api.post(`/weddings/${w.wedding_id}/events`, payload);
      }
      toast.success(editing ? "Event updated" : "Event created");
      setShowForm(false);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  }
  async function remove(id) {
    if (!window.confirm("Delete this event? Guest RSVPs for it will also be removed.")) return;
    await api.delete(`/weddings/${w.wedding_id}/events/${id}`);
    load();
  }

  async function toggleInvite(guestId, eventId) {
    const cur = invitesByGuest[guestId] || [];
    const next = cur.includes(eventId) ? cur.filter((x) => x !== eventId) : [...cur, eventId];
    setInvitesByGuest({ ...invitesByGuest, [guestId]: next });
    try {
      await api.post(`/weddings/${w.wedding_id}/guests/${guestId}/event-invites`, { event_ids: next });
    } catch (err) { toast.error(formatApiError(err)); }
  }

  return (
    <div data-testid="events-page">
      <div className="mb-8 flex items-end justify-between gap-4 flex-wrap">
        <div>
          <p className="text-[11px] uppercase tracking-[0.22em] text-[var(--gold)] mb-1 flex items-center gap-2">
            <Calendar className="w-3 h-3" /> Events
          </p>
          <h1 className="font-serif text-4xl md:text-5xl">Multi-event <span className="font-script text-[var(--gold)]">Itinerary</span></h1>
          <p className="text-sm text-[var(--muted)] mt-2 max-w-xl">
            Add rehearsal, ceremony, reception, brunch — and decide which guests are invited to which events. Each guest only sees their own events when they RSVP.
          </p>
        </div>
        <div className="flex gap-2">
          {events.length > 0 && (
            <button
              onClick={() => { setShowInvites(true); loadInvites(); }}
              data-testid="open-invites-btn"
              className="btn-outline-gold text-xs"
            >
              <Users className="w-3 h-3" /> Manage guest invites
            </button>
          )}
          <button onClick={openNew} data-testid="new-event-btn" className="btn-gold text-xs"><Plus className="w-3 h-3" /> New event</button>
        </div>
      </div>

      {events.length === 0 ? (
        <div className="text-center py-16 border border-dashed border-[var(--line)] rounded-2xl">
          <Calendar className="w-10 h-10 mx-auto text-[var(--gold)] mb-3" />
          <p className="font-serif text-2xl">No events yet</p>
          <p className="text-sm text-[var(--muted)] mt-2 mb-5">Start with the Ceremony — you can add more events anytime.</p>
          <button onClick={openNew} className="btn-gold text-xs">Create first event</button>
        </div>
      ) : (
        <div className="space-y-3" data-testid="events-list">
          {events.map((e) => (
            <div key={e.event_id} className="bg-white border border-[var(--line)] rounded-2xl p-5 flex items-center gap-4" data-testid={`event-${e.event_id}`}>
              <div className="w-1.5 h-16 rounded-full shrink-0" style={{ background: e.color_accent || "#C8A96B" }} />
              <div className="flex-1 min-w-0">
                <p className="text-[10px] uppercase tracking-[0.18em] text-[var(--gold)]">{TYPES.find((t) => t.v === e.type)?.l || e.type}</p>
                <p className="font-serif text-xl mt-1">{e.name}</p>
                <div className="flex flex-wrap gap-x-5 gap-y-1 mt-1 text-xs text-[var(--muted)]">
                  {e.event_datetime && <span><Calendar className="w-3 h-3 inline mr-1" />{new Date(e.event_datetime).toLocaleString()}</span>}
                  {e.venue && <span><MapPin className="w-3 h-3 inline mr-1" />{e.venue}</span>}
                  {e.dress_code && <span>👗 {e.dress_code}</span>}
                </div>
              </div>
              <button onClick={() => openEdit(e)} data-testid={`edit-${e.event_id}`} className="text-xs text-[var(--gold)] px-3">Edit</button>
              <button onClick={() => remove(e.event_id)} data-testid={`delete-${e.event_id}`} className="text-[var(--muted)] hover:text-red-600 p-2"><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
        </div>
      )}

      {/* Form modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" data-testid="event-form">
          <form onSubmit={save} className="bg-white rounded-3xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-[var(--line)] px-6 py-4 flex items-center justify-between">
              <p className="font-serif text-xl">{editing ? "Edit Event" : "New Event"}</p>
              <button type="button" onClick={() => setShowForm(false)} className="text-[var(--muted)]"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-6 space-y-3">
              <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} data-testid="event-type" className="input-luxe">
                {TYPES.map((t) => <option key={t.v} value={t.v}>{t.l}</option>)}
              </select>
              <input required placeholder="Name (e.g. 'Rehearsal at La Bella')" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="event-name" className="input-luxe" />
              <input type="datetime-local" value={form.event_datetime?.slice(0, 16) || ""} onChange={(e) => setForm({ ...form, event_datetime: e.target.value ? new Date(e.target.value).toISOString() : "" })} data-testid="event-datetime" className="input-luxe" />
              <input placeholder="Venue" value={form.venue} onChange={(e) => setForm({ ...form, venue: e.target.value })} data-testid="event-venue" className="input-luxe" />
              <input placeholder="Address" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="input-luxe" />
              <input placeholder="Dress code" value={form.dress_code} onChange={(e) => setForm({ ...form, dress_code: e.target.value })} className="input-luxe" />
              <textarea placeholder="Description / agenda" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3} className="input-luxe resize-none" />
              <div className="flex items-center gap-3">
                <label className="text-[11px] uppercase tracking-[0.16em] text-[var(--muted)]">Accent</label>
                <input type="color" value={form.color_accent} onChange={(e) => setForm({ ...form, color_accent: e.target.value })} className="w-10 h-10 rounded-full border-0 cursor-pointer" />
              </div>
              <button type="submit" disabled={busy} data-testid="event-save" className="btn-gold w-full"><Save className="w-3 h-3" /> {busy ? "Saving…" : "Save event"}</button>
            </div>
          </form>
        </div>
      )}

      {/* Invites manager */}
      {showInvites && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" data-testid="invites-manager">
          <div className="bg-white rounded-3xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-[var(--line)] px-6 py-4 flex items-center justify-between">
              <p className="font-serif text-xl">Who's invited to what</p>
              <button onClick={() => setShowInvites(false)} className="text-[var(--muted)]"><X className="w-5 h-5" /></button>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-[10px] uppercase tracking-[0.18em] text-[var(--muted)] border-b border-[var(--line)]">
                    <th className="px-4 py-3 sticky left-0 bg-white">Guest</th>
                    {events.map((e) => (
                      <th key={e.event_id} className="px-3 py-3 text-center">{e.name}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {guests.map((g) => (
                    <tr key={g.guest_id} className="border-b border-[var(--line)]" data-testid={`invite-row-${g.guest_id}`}>
                      <td className="px-4 py-3 sticky left-0 bg-white whitespace-nowrap">{g.name}</td>
                      {events.map((e) => {
                        const checked = (invitesByGuest[g.guest_id] || []).includes(e.event_id);
                        return (
                          <td key={e.event_id} className="px-3 py-3 text-center">
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={() => toggleInvite(g.guest_id, e.event_id)}
                              data-testid={`invite-${g.guest_id}-${e.event_id}`}
                              className="w-4 h-4 accent-[var(--gold)] cursor-pointer"
                            />
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
              {guests.length === 0 && <p className="text-center text-sm text-[var(--muted)] py-10">No guests yet. Add some on the Guests page first.</p>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
